import { useCallback, useEffect, useState } from "react";
import type { AdsActivityFreshAchievement } from "@/lib/adsAssistantClientEngagement";
import { fetchUserAchievements, revokeUserAchievement } from "@/lib/userAchievementsClient";

export function useUserAchievements(args: {
  enabled: boolean;
  scopedOrgId: string | null;
  viewUserId?: string | null;
}) {
  const [achievements, setAchievements] = useState<AdsActivityFreshAchievement[]>([]);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!args.enabled || !args.scopedOrgId) {
      setAchievements([]);
      setDisplayName(null);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const r = await fetchUserAchievements(
        args.viewUserId ? { userId: args.viewUserId } : undefined,
      );
      if (!r.ok) {
        setError(r.error);
        setAchievements([]);
        setDisplayName(null);
      } else {
        setError(null);
        setAchievements(r.data.achievements ?? []);
        setDisplayName(r.data.display_name ?? null);
      }
    } finally {
      setLoading(false);
    }
  }, [args.enabled, args.scopedOrgId, args.viewUserId]);

  useEffect(() => {
    if (!args.enabled) return;
    void refresh();
  }, [args.enabled, refresh]);

  const revoke = useCallback(
    async (achievementId: string) => {
      setRevokingId(achievementId);
      try {
        const r = await revokeUserAchievement(achievementId);
        if (!r.ok) {
          setError(r.error);
          return false;
        }
        setAchievements((prev) => prev.filter((a) => a.id !== achievementId));
        return true;
      } finally {
        setRevokingId(null);
      }
    },
    [],
  );

  return { achievements, displayName, loading, error, revokingId, refresh, revoke };
}
